import { useMemo, useState } from 'react';
import { useAuth } from '@clerk/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Check, Loader2, ShoppingCart, X } from 'lucide-react';
import { useToast } from '@/shared/contexts/ToastContext';
import {
  fetchPremiumLeads,
  postPremiumPurchase,
  type PremiumLeadRow,
  type WalletResponse
} from './api/accountApi';
import { ACCOUNT_TABLE_CHECKBOX_CLASS } from './accountStyles';
import { propertyTypeCell } from './propertyTypeDisplay';

const AccountPremiumLeadsPage = () => {
  const { getToken, userId, sessionId } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const q = useQuery({
    queryKey: ['account', 'premium-leads', userId, sessionId, getToken],
    queryFn: () => fetchPremiumLeads(getToken),
    enabled: Boolean(userId),
    staleTime: 30_000,
    refetchOnWindowFocus: false
  });

  const buy = useMutation({
    mutationFn: (leadIds: string[]) => postPremiumPurchase(getToken, leadIds),
    onSuccess: data => {
      notify(
        'success',
        `Purchased ${data.created} lead(s). Debited $${data.debited.toFixed(2)}.`
      );
      setPicked(new Set());
      void qc.invalidateQueries({
        queryKey: ['account', 'premium-leads', userId, sessionId]
      });
      const walletKey = ['account', 'wallet', userId, sessionId] as const;
      qc.setQueryData<WalletResponse | undefined>(walletKey, prev =>
        prev ? { ...prev, balance: data.balance } : prev
      );
      void qc.invalidateQueries({ queryKey: walletKey });
      void qc.refetchQueries({ queryKey: walletKey, exact: true });
      void qc.invalidateQueries({ queryKey: ['account', 'leads'] });
    },
    onError: e => {
      notify(
        'error',
        e instanceof Error ? e.message : 'Premium purchase failed'
      );
    }
  });

  const rows = useMemo(() => q.data?.leads ?? [], [q.data?.leads]);
  const [kindFilter, setKindFilter] = useState<'all' | 'buyer' | 'lease'>(
    'all'
  );
  const [sortBy, setSortBy] = useState<'age_asc' | 'age_desc'>('age_asc');
  const [pageSize, setPageSize] = useState<25 | 50>(25);
  const [page, setPage] = useState(0);

  const filteredRows = useMemo(() => {
    let out = rows;
    if (kindFilter !== 'all') out = out.filter(r => r.leadType === kindFilter);
    out = [...out].sort((a, b) =>
      sortBy === 'age_asc' ? a.ageDays - b.ageDays : b.ageDays - a.ageDays
    );
    return out;
  }, [rows, kindFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pagedRows = useMemo(
    () =>
      filteredRows.slice(safePage * pageSize, safePage * pageSize + pageSize),
    [filteredRows, safePage, pageSize]
  );

  const selectedRows = useMemo(
    () => filteredRows.filter(r => picked.has(r.leadId)),
    [filteredRows, picked]
  );
  const total = selectedRows.reduce((s, r) => s + r.priceCad, 0);

  const formatAge = (days: number): string => {
    if (!Number.isFinite(days) || days < 0) return '—';
    if (days === 0) return 'today';
    if (days < 7) return days === 1 ? '1 day ago' : `${days} days ago`;
    const weeks = Math.round(days / 7);
    if (weeks < 4) return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
    const months = Math.max(1, Math.round(days / 30));
    return months === 1 ? '1 month ago' : `${months} months ago`;
  };

  const toggle = (leadId: string) => {
    setPicked(prev => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId);
      else next.add(leadId);
      return next;
    });
  };

  const renderPaginationControls = () => (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-600">Per page</span>
        <select
          value={pageSize}
          onChange={e => {
            const next = Number(e.target.value) === 50 ? 50 : 25;
            setPageSize(next as 25 | 50);
            setPage(0);
          }}
          className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900"
        >
          <option value={25}>25</option>
          <option value={50}>50</option>
        </select>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={safePage === 0}
          onClick={() => setPage(p => Math.max(0, p - 1))}
          className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 disabled:opacity-40"
        >
          Prev
        </button>
        <span className="text-xs text-slate-500">
          Page {safePage + 1} of {totalPages}
        </span>
        <button
          type="button"
          disabled={safePage >= totalPages - 1}
          onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
          className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-slate-900">Purchase Leads</h1>
        <p className="mt-1 text-sm text-slate-600">
          Fresh in-territory leads at standard pricing.
        </p>
      </div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2">
        <p className="text-sm text-slate-700">
          Selected {selectedRows.length} lead(s) · Total ${total.toFixed(2)}
        </p>
        <button
          type="button"
          disabled={!selectedRows.length || buy.isPending}
          onClick={() => {
            if (
              !window.confirm(
                `Buy ${selectedRows.length} lead(s) for $${total.toFixed(2)}?`
              )
            ) {
              return;
            }
            buy.mutate(selectedRows.map(r => r.leadId));
          }}
          className="inline-flex items-center gap-2 rounded-lg bg-[#2b6cff] px-4 py-1.5 text-sm font-semibold text-white hover:bg-[#2563eb] disabled:opacity-50"
        >
          <CreditCard className="h-4 w-4" aria-hidden />
          Buy selected
        </button>
      </div>
      {q.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2
            className="h-8 w-8 animate-spin text-slate-500"
            aria-hidden
          />
        </div>
      ) : q.isError ? (
        <p className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {q.error instanceof Error
            ? q.error.message
            : 'Could not load premium leads.'}
        </p>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-600">
              {rows.length} premium lead(s) available · page {safePage + 1} of{' '}
              {totalPages} · {selectedRows.length} selected
            </p>
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <label className="flex items-center gap-1">
                <span className="text-slate-600">Type</span>
                <select
                  value={kindFilter}
                  onChange={e => {
                    setKindFilter(e.target.value as 'all' | 'buyer' | 'lease');
                    setPage(0);
                  }}
                  className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900"
                >
                  <option value="all">All leads</option>
                  <option value="buyer">Buyer leads</option>
                  <option value="lease">Renter leads</option>
                </select>
              </label>
              <label className="flex items-center gap-1">
                <span className="text-slate-600">Sort</span>
                <select
                  value={sortBy}
                  onChange={e =>
                    setSortBy(e.target.value as 'age_asc' | 'age_desc')
                  }
                  className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900"
                >
                  <option value="age_asc">Oldest first</option>
                  <option value="age_desc">Newest first</option>
                </select>
              </label>
            </div>
          </div>
          <div className="mb-3">{renderPaginationControls()}</div>
          <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-3 py-2">Buy</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Phone</th>
                  <th className="px-3 py-2">MLS / Listing</th>
                  <th className="px-3 py-2">Property type</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Age</th>
                  <th className="px-3 py-2">Territory</th>
                  <th className="px-3 py-2">Price</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((r: PremiumLeadRow) => (
                  <tr key={r.leadId} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className={ACCOUNT_TABLE_CHECKBOX_CLASS}
                          checked={picked.has(r.leadId)}
                          onChange={() => toggle(r.leadId)}
                        />
                        <button
                          type="button"
                          onClick={() => toggle(r.leadId)}
                          className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-xs ${
                            picked.has(r.leadId)
                              ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                              : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                          }`}
                          title={
                            picked.has(r.leadId) ? 'In cart' : 'Add to cart'
                          }
                        >
                          {picked.has(r.leadId) ? (
                            <Check className="h-3 w-3" aria-hidden />
                          ) : (
                            <ShoppingCart className="h-3 w-3" aria-hidden />
                          )}
                          <span>
                            {picked.has(r.leadId) ? 'In cart' : 'Add to cart'}
                          </span>
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-emerald-700">
                      <Check className="h-4 w-4" aria-hidden />
                    </td>
                    <td className="px-3 py-2 text-emerald-700">
                      <Check className="h-4 w-4" aria-hidden />
                    </td>
                    <td className="px-3 py-2">
                      {r.hasPhone ? (
                        <span className="text-emerald-700">
                          <Check className="h-4 w-4" aria-hidden />
                        </span>
                      ) : (
                        <span className="text-rose-600">
                          <X className="h-4 w-4" aria-hidden />
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {r.mlsNumber ?? r.listingId ?? r.leadId}
                    </td>
                    <td className="px-3 py-2">
                      {propertyTypeCell(r.propertyType)}
                    </td>
                    <td className="px-3 py-2">
                      {r.leadType === 'buyer' ? 'Buyer lead' : 'Renter lead'}
                    </td>
                    <td className="px-3 py-2">{formatAge(r.ageDays)}</td>
                    <td className="px-3 py-2">
                      <span className="rounded bg-emerald-100 px-2 py-0.5 text-emerald-800">
                        inside
                      </span>
                    </td>
                    <td className="px-3 py-2">${r.priceCad.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3">{renderPaginationControls()}</div>
          <div className="mt-3 flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3">
            <p className="text-sm text-slate-700">
              Selected {selectedRows.length} lead(s) · Total ${total.toFixed(2)}
            </p>
            <button
              type="button"
              disabled={!selectedRows.length || buy.isPending}
              onClick={() => {
                if (
                  !window.confirm(
                    `Buy ${selectedRows.length} lead(s) for $${total.toFixed(2)}?`
                  )
                ) {
                  return;
                }
                buy.mutate(selectedRows.map(r => r.leadId));
              }}
              className="rounded-lg bg-[#2b6cff] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2563eb] disabled:opacity-50"
            >
              {buy.isPending ? 'Processing…' : 'Buy selected'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AccountPremiumLeadsPage;
